import {Button} from "@zendeskgarden/react-buttons";
import {Field, Field as FormField, Input as FormInput, Label as FormLabel, Label, Toggle} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {TooltipModal} from "@zendeskgarden/react-modals";
import {ReactElement, useMemo, useState} from "react";
import {toast} from "react-toastify";
import {useCreateOperationalDriversMutation, VersionType} from "../../../__generated__/generated_types";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {COLORS} from "../../../constants/Colors";
import {OperationalMetricTypeIds} from "../../../constants/OperationalMetricTypeIds";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {Property} from "../../../contexts/properties/logic";
import MinMaxFields from "../../workflows/account/formula-bar/components/formula-nodes/components/MinMaxFields";
import * as css from "./styles/OperationalTab.module.scss";

const MANUAL_ENTRY_METRIC_ID = "MANUAL_ENTRY";

export interface CreateOperationalDriverTooltipModalProps {
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    properties: Property[];
    locksLookup: Set<string>;
    operationalMetricTypes: {id: string, name: string}[];
    referenceElement: HTMLButtonElement;
    onClose: () => void;
    onCreated: () => void;
}

export function CreateOperationalDriverTooltipModal(props: CreateOperationalDriverTooltipModalProps): ReactElement {
    const {budgetYear, versionType, properties, locksLookup, operationalMetricTypes, referenceElement, onClose, onCreated} = props;

    const [createOperationalDrivers, {loading: createLoading}] = useCreateOperationalDriversMutation();
    const [name, setName] = useState<string>("");
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);
    const [selectedMetricTypeId, setSelectedMetricTypeId] = useState<string | undefined>(undefined);
    const [selectedSecondaryMetricTypeId, setSelectedSecondaryMetricTypeId] = useState<string>(MANUAL_ENTRY_METRIC_ID);
    const [isCumSum, setIsCumSum] = useState(false);
    const [isProrated, setIsProrated] = useState(true);
    const [minValue, setMinValue] = useState<string | null>(null);
    const [maxValue, setMaxValue] = useState<string | null>(null);
    const [minMaxError, setMinMaxError] = useState<string | null>(null);

    const {accounts} = useChartOfAccounts();

    const unlockedProperties = useMemo(() => properties.filter(p => !locksLookup.has(p.id)),[properties, locksLookup]);
    const allPropertiesLocked = unlockedProperties.length === 0;

    const secondaryMetricOptions = useMemo(() => {
        return [{id: MANUAL_ENTRY_METRIC_ID, name: "Manual Entry"}, ...operationalMetricTypes];
    }, [operationalMetricTypes]);

    function handleSubmit(): void {
        if (!name || selectedPropertyIds.length === 0 || !selectedAccountId || !selectedMetricTypeId) {
            toast.error("Please fill in all fields");
            return;
        }

        createOperationalDrivers({
            variables: {
                budgetYear,
                versionTypes: [versionType],
                propertyIds: selectedPropertyIds,
                destinationAccountId: selectedAccountId,
                operationalMetricTypeId: selectedMetricTypeId,
                secondaryOperationalMetricTypeId: selectedSecondaryMetricTypeId === MANUAL_ENTRY_METRIC_ID ? null : selectedSecondaryMetricTypeId,
                name,
                cumSum: isCumSum,
                nonProrated: !isProrated,
                minValue: minValue,
                maxValue: maxValue,
            }
        }).then(res => {
            if (res.data?.createOperationalDrivers) {
                toast.success("Operational driver created successfully");
                onCreated();
            } else {
                toast.error("Failed to create operational driver");
            }
        }).catch(() => {
            toast.error("Failed to create operational driver");
        });
    }

    return (
        <TooltipModal
            referenceElement={referenceElement}
            onClose={onClose}
            placement="start-top"
        >
            <TooltipModal.Title>Create Operational Driver</TooltipModal.Title>
            <TooltipModal.Body>
                <div style={{display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "20rem"}}>
                    <FormField>
                        <FormLabel>Name</FormLabel>
                        <FormInput
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </FormField>
                    <CustomZDDropdown
                        label="Properties"
                        allOption
                        isMulti
                        options={unlockedProperties.map(p => ({value: p.id, label: p.name}))}
                        initialSelectedItems={selectedPropertyIds}
                        applySelectedItems={items => setSelectedPropertyIds(items)}
                        openDropdownPlaceholder="Search properties..."
                        closedDropdownPlaceholder="Select properties"
                        isError={undefined}
                    />
                    <CustomZDDropdown
                        label="Destination Account"
                        options={accounts.map(a => ({value: a.id, label: `${a.accountNumber} - ${a.name}`}))}
                        initialSelectedItems={selectedAccountId ? [selectedAccountId] : []}
                        applySelectedItems={items => setSelectedAccountId(items[0])}
                        openDropdownPlaceholder="Search accounts..."
                        closedDropdownPlaceholder="Select an account"
                        isError={undefined}
                    />
                    <CustomZDDropdown
                        label="Operational Metric"
                        options={operationalMetricTypes.map(m => ({value: m.id, label: m.name}))}
                        initialSelectedItems={selectedMetricTypeId ? [selectedMetricTypeId] : []}
                        applySelectedItems={items => setSelectedMetricTypeId(items[0])}
                        openDropdownPlaceholder="Search metrics..."
                        closedDropdownPlaceholder="Select a metric"
                        isError={undefined}
                    />
                    <CustomZDDropdown
                        label="Secondary Operational Metric"
                        options={secondaryMetricOptions.map(m => ({value: m.id, label: m.name}))}
                        initialSelectedItems={[selectedSecondaryMetricTypeId]}
                        applySelectedItems={items => setSelectedSecondaryMetricTypeId(items[0])}
                        openDropdownPlaceholder="Search metrics..."
                        closedDropdownPlaceholder="Select a metric"
                        isError={undefined}
                    />
                    {allPropertiesLocked &&
                        <span style={{color: COLORS.NEGATIVE_COLOR, fontSize: "0.85rem"}}>
                            All properties are locked for the selected forecast period.
                        </span>
                    }
                    <div className={css.createDriverBottomRow}>
                        <Field style={{marginTop: "1rem"}}>
                            <Toggle
                                isCompact
                                checked={isCumSum}
                                onChange={evt => setIsCumSum(evt.target.checked)}
                            >
                                <Label>Ramp-up</Label>
                            </Toggle>
                        </Field>
                        {selectedMetricTypeId === OperationalMetricTypeIds.EMPLOYEE_COUNT &&
                            <Field style={{marginTop: "1rem"}}>
                                <Toggle
                                    isCompact
                                    checked={isProrated}
                                    onChange={evt => setIsProrated(evt.target.checked)}
                                >
                                    <Label>{isProrated ? "Prorated" : "Full Count"}</Label>
                                </Toggle>
                            </Field>
                        }
                        <MinMaxFields
                            minValue={minValue}
                            maxValue={maxValue}
                            hideHintText
                            onChange={vals => {
                                setMinValue(vals.minValue);
                                setMaxValue(vals.maxValue);
                            }}
                            onErrorUpdate={setMinMaxError}
                        />
                    </div>
                    <Button
                        isPrimary
                        size="small"
                        onClick={handleSubmit}
                        disabled={allPropertiesLocked || createLoading || !name || selectedPropertyIds.length === 0 || !selectedAccountId || !selectedMetricTypeId || minMaxError !== null}
                    >
                        {createLoading
                            ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading" />
                            : <span>Create</span>
                        }
                    </Button>
                </div>
            </TooltipModal.Body>
            <TooltipModal.Close aria-label="Close" />
        </TooltipModal>
    );
}
